/**
 * Per-module CSV contracts for Manage Asset Catalog (template + export mappers).
 * Keep in sync with actionlabs-services App\Support\Sima\ContentCsvSchema.
 */

export const ASSET_CATALOG_IMPORT_ORDER_COMMENTS = [
  'Import order: 1) Categories 2) Functions 3) Brands 4) Models 5) Spec option lists (any order) 6) Tags (any order)',
  'System roots (machine-types, ram, cpu, …) are seeded when the client database is created — do not import root rows here.',
  'Do not auto-create missing parents — import Categories/Brands before Functions/Models.',
  'Active: use 1/0, true/false, yes/no, or Active/Inactive (blank defaults to Active).',
] as const

export type AssetCatalogCsvModule =
  | 'categories'
  | 'functions'
  | 'brands'
  | 'brand-models'
  | 'tags'
  | 'ram'
  | 'disk-space'
  | 'ms-office'
  | 'sw-provider'
  | 'cpu'
  | 'processor'
  | 'os-version'

type ModuleCsvDef = {
  headers: readonly string[]
  exampleRow: readonly string[]
  comments: readonly string[]
  filename: string
  mapRow: (item: Record<string, unknown>) => string[]
}

function formatActive(value: unknown): string {
  if (value === true || value === 1 || value === '1') return '1'
  if (value === false || value === 0 || value === '0') return '0'
  const s = String(value ?? '').trim().toLowerCase()
  if (s === 'active' || s === 'true' || s === 'yes') return '1'
  if (s === 'inactive' || s === 'false' || s === 'no') return '0'
  return s === '' ? '1' : String(value)
}

function str(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

function brandCategoriesPipe(item: Record<string, unknown>): string {
  const names = item.category_names
  if (typeof names === 'string' && names.trim()) {
    return names
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean)
      .join('|')
  }
  const raw = item.categories
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) {
        return parsed
          .map((entry) => {
            if (entry && typeof entry === 'object' && 'label' in entry) {
              return String((entry as { label?: string }).label ?? '')
            }
            return ''
          })
          .filter(Boolean)
          .join('|')
      }
    } catch {
      return raw.replace(/,/g, '|')
    }
  }
  return ''
}

function relationName(value: unknown, fallbackKeys: string[] = ['name']): string {
  if (value && typeof value === 'object') {
    for (const key of fallbackKeys) {
      const v = (value as Record<string, unknown>)[key]
      if (v !== undefined && v !== null && String(v) !== '') return String(v)
    }
  }
  return str(value)
}

const SPEC_COMMENTS = ['Upsert key: Slug under this option-list parent.'] as const

function specDef(filename: string, exampleName: string, exampleSlug: string): ModuleCsvDef {
  return {
    headers: ['Name', 'Slug', 'Description', 'Active'],
    exampleRow: [exampleName, exampleSlug, `${exampleName} option`, '1'],
    comments: [...ASSET_CATALOG_IMPORT_ORDER_COMMENTS, ...SPEC_COMMENTS],
    filename,
    mapRow: (item) => [
      str(item.name),
      str(item.slug),
      str(item.descriptions ?? item.description),
      formatActive(item.active),
    ],
  }
}

export const ASSET_CATALOG_CSV: Record<AssetCatalogCsvModule, ModuleCsvDef> = {
  categories: {
    headers: ['Name', 'Slug', 'Description', 'Active'],
    exampleRow: ['POS Terminal', 'pos-terminal', 'Point of sale terminal', '1'],
    comments: [
      ...ASSET_CATALOG_IMPORT_ORDER_COMMENTS,
      'Upsert key: Slug (under machine types). Blank Slug is derived from Name.',
    ],
    filename: 'categories_template.csv',
    mapRow: (item) => [
      str(item.name),
      str(item.slug),
      str(item.descriptions ?? item.description),
      formatActive(item.active),
    ],
  },
  functions: {
    headers: ['Name', 'Slug', 'Parent Category', 'Description', 'Active'],
    exampleRow: ['Checkout', 'checkout', 'POS Terminal', 'Checkout function', '1'],
    comments: [
      ...ASSET_CATALOG_IMPORT_ORDER_COMMENTS,
      'Upsert key: Slug under Parent Category.',
      'Parent Category: machine-type Name or Slug (must already exist).',
    ],
    filename: 'functions_template.csv',
    mapRow: (item) => [
      str(item.name),
      str(item.slug),
      str(item.parent_category_name) ||
        str(item.parent_name) ||
        relationName(item.parent ?? item.get_parent ?? item.parent_category, ['name', 'label']),
      str(item.descriptions ?? item.description),
      formatActive(item.active),
    ],
  },
  brands: {
    headers: ['Name', 'Description', 'Categories', 'Active'],
    exampleRow: ['Ingenico', 'Payment terminals', 'POS Terminal|Printer', '1'],
    comments: [
      ...ASSET_CATALOG_IMPORT_ORDER_COMMENTS,
      'Upsert key: Name.',
      'Categories: pipe-separated machine-type names or slugs (must already exist).',
    ],
    filename: 'brands_template.csv',
    mapRow: (item) => [
      str(item.name),
      str(item.descriptions ?? item.description),
      brandCategoriesPipe(item),
      formatActive(item.active),
    ],
  },
  'brand-models': {
    headers: ['Name', 'Slug', 'Brand', 'Category', 'Description', 'Active'],
    exampleRow: ['POS Terminal 001', 'pos-terminal-001', 'Ingenico', 'POS Terminal', 'Model 001', '1'],
    comments: [
      ...ASSET_CATALOG_IMPORT_ORDER_COMMENTS,
      'Upsert key: Name + Brand + Category.',
      'Brand and Category must already exist (name or slug).',
    ],
    filename: 'brand_models_template.csv',
    mapRow: (item) => [
      str(item.name),
      str(item.slug),
      str(item.brand_name) || relationName(item.brand, ['name']),
      str(item.category_name) || relationName(item.category, ['name']),
      str(item.descriptions ?? item.description),
      formatActive(item.active),
    ],
  },
  tags: {
    headers: ['Name', 'Slug', 'Description'],
    exampleRow: ['Critical', 'critical', 'High priority assets'],
    comments: [
      ...ASSET_CATALOG_IMPORT_ORDER_COMMENTS,
      'Upsert key: Slug. Blank Slug is derived from Name.',
      'Tags have no Active column.',
    ],
    filename: 'tags_template.csv',
    mapRow: (item) => [
      str(item.name),
      str(item.slug),
      str(item.descriptions ?? item.description),
    ],
  },
  ram: specDef('ram_template.csv', '8GB DDR4', '8gb-ddr4'),
  'disk-space': specDef('disk_space_template.csv', '256GB SSD', '256gb-ssd'),
  'ms-office': specDef('ms_office_template.csv', 'MS Office 2021', 'ms-office-2021'),
  'sw-provider': specDef('sw_provider_template.csv', 'Ingenico', 'ingenico'),
  cpu: specDef('cpu_template.csv', 'Quad-Core 2.4GHz', 'quad-core-2-4ghz'),
  processor: specDef('processor_template.csv', 'Intel Core i7', 'intel-core-i7'),
  'os-version': specDef('os_version_template.csv', 'Windows 10 Pro', 'windows-10-pro'),
}

export function getAssetCatalogCsvModule(module: AssetCatalogCsvModule): ModuleCsvDef {
  return ASSET_CATALOG_CSV[module]
}

/** Map API base path segment to CSV module key. */
export function assetCatalogModuleFromApiBase(apiBase: string): AssetCatalogCsvModule | null {
  const segment = apiBase.replace(/^\/master-data\//, '').replace(/\/$/, '')
  if (segment in ASSET_CATALOG_CSV) {
    return segment as AssetCatalogCsvModule
  }
  return null
}
